/**
 * Menu lateral do redesign (JOR4-7365).
 *
 * Substitui o dxTreeView. A troca não é estética: o modo colapsado com ícones e
 * o submenu flutuante no hover (RN04) não saem do TreeView sem lutar contra ele.
 *
 * A montagem da lista continua em montarMenu.ts — este componente só desenha.
 */

import { ChevronDown, ChevronRight, Search, X } from "lucide-react";
import {
    useEffect,
    useMemo,
    useRef,
    useState,
    type KeyboardEvent,
} from "react";
import { createRoot, Root } from "react-dom/client";

import {
    achatarVisiveis,
    contemClasseAtiva,
    ehGrupo,
    indiceDoPai,
    montarArvore,
    type No,
} from "./arvoreMenu";
import { FlyoutSubmenu } from "./FlyoutSubmenu";
import {
    TAMANHO_ICONE_GRUPO,
    TAMANHO_ICONE_ITEM,
    iconeDoMenu,
} from "./iconesMenu";
import { filterMenuItems, type ItemMenu } from "./montarMenu";

export interface EstadoMenuLateral {
    itens: ItemMenu[];
    /** TLA_CLASSEGRID da tela em foco. Destaca o item correspondente (RN03). */
    classeAtiva: string | null;
    /** Modo de ícones. Segue o estado do dxDrawer, que o hospeda. */
    colapsado: boolean;
    aoAbrirTela: (item: ItemMenu) => void;
}

const Icone = ({ item, tamanho }: { item: ItemMenu; tamanho: number }) => {
    const Componente = iconeDoMenu(item);
    return <Componente size={tamanho} />;
};

/**
 * Navegação por teclado da árvore.
 *
 * O dxTreeView substituído era UMA parada de Tab: entrava-se nele uma vez e as
 * setas faziam o resto (`focusStateEnabled: true`, e o CollectionWidget de que
 * ele herda trata ←/→/↑/↓). Com um <button> por item o Tab passaria pelos 58
 * itens do menu antes de chegar ao conteúdo da página, o que a RN01 não permite
 * perder. Daí o roving tabindex: só um item é focável por vez, e as setas movem
 * o foco.
 */
interface Navegacao {
    /** Único item com tabIndex 0 — o resto fica em -1. */
    focavelId: string | null;
    aoFocar: (id: string) => void;
    aoTeclar: (evento: KeyboardEvent<HTMLButtonElement>, id: string) => void;
}

/** Props que todo nó repassa ao <button> para participar da navegação. */
const propsNavegacao = (nav: Navegacao, id: string) => ({
    "data-menu-id": id,
    tabIndex: nav.focavelId === id ? 0 : -1,
    onFocus: () => nav.aoFocar(id),
    onKeyDown: (evento: KeyboardEvent<HTMLButtonElement>) =>
        nav.aoTeclar(evento, id),
});

const ItemFolha = ({
    no,
    classeAtiva,
    aoAbrirTela,
    nav,
}: {
    no: No;
    classeAtiva: string | null;
    aoAbrirTela: (item: ItemMenu) => void;
    nav: Navegacao;
}) => {
    const ativo = no.item.classInvoke === classeAtiva;
    return (
        <button
            type="button"
            role="treeitem"
            aria-selected={ativo}
            className={`menu-ds-item${ativo ? " is-ativo" : ""}`}
            onClick={() => aoAbrirTela(no.item)}
            title={no.item.text}
            {...propsNavegacao(nav, String(no.item.ID))}
        >
            <span className="menu-ds-item-icone">
                <Icone item={no.item} tamanho={TAMANHO_ICONE_ITEM} />
            </span>
            <span className="menu-ds-rotulo">{no.item.text}</span>
        </button>
    );
};

interface PropsNo {
    no: No;
    /** Recebido como função, e não como booleano: cada nó resolve o seu. */
    estaAberto: (no: No) => boolean;
    alternar: (id: string) => void;
    classeAtiva: string | null;
    aoAbrirTela: (item: ItemMenu) => void;
    nav: Navegacao;
}

const Grupo = ({
    no,
    estaAberto,
    alternar,
    classeAtiva,
    aoAbrirTela,
    nav,
}: PropsNo) => {
    const aberto = estaAberto(no);
    // O grupo herda o destaque do filho ativo: no protótipo o usuário continua
    // sabendo onde está mesmo com o grupo fechado.
    const temAtivo = contemClasseAtiva(no, classeAtiva);

    // No dxTreeView, clicar num grupo que também tinha tela expandia e abria a
    // tela. Nenhum grupo em produção tem TLA_CLASSEGRID, mas o cadastro
    // permite — e perder a tela em silêncio seria pior que o clique duplo.
    const aoClicar = () => {
        alternar(String(no.item.ID));
        if (no.item.classInvoke) aoAbrirTela(no.item);
    };

    return (
        <div className="menu-ds-grupo-bloco">
            <button
                type="button"
                role="treeitem"
                aria-expanded={aberto}
                className={`menu-ds-grupo${temAtivo ? " is-ativo" : ""}`}
                onClick={aoClicar}
                title={no.item.text}
                {...propsNavegacao(nav, String(no.item.ID))}
            >
                <span className="menu-ds-grupo-icone">
                    <Icone item={no.item} tamanho={TAMANHO_ICONE_GRUPO} />
                </span>
                <span className="menu-ds-rotulo">{no.item.text}</span>
                {aberto ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            </button>

            {aberto && (
                <div className="menu-ds-filhos" role="group">
                    {no.filhos.map((filho) => (
                        <NoMenu
                            key={filho.item.ID}
                            no={filho}
                            estaAberto={estaAberto}
                            alternar={alternar}
                            classeAtiva={classeAtiva}
                            aoAbrirTela={aoAbrirTela}
                            nav={nav}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

/**
 * Um nó é grupo quando tem filhos. Não dá para usar `classInvoke` vazio como
 * critério: existe grupo cadastrado com classe, e existe tela sem classe, que
 * não abriria nada se fosse tratada como item.
 */
const NoMenu = (props: PropsNo) =>
    ehGrupo(props.no) ? (
        <Grupo {...props} />
    ) : (
        <ItemFolha
            no={props.no}
            classeAtiva={props.classeAtiva}
            aoAbrirTela={props.aoAbrirTela}
            nav={props.nav}
        />
    );

/** Atraso entre sair do ícone e fechar o painel. */
const MS_PARA_FECHAR = 150;

/**
 * Modo colapsado (RN04): só os ícones das raízes. Passar o mouse sobre um grupo
 * abre o submenu flutuante; uma raiz que é tela abre direto no clique.
 */
const MenuColapsado = ({
    arvore,
    classeAtiva,
    aoAbrirTela,
}: {
    arvore: No[];
    classeAtiva: string | null;
    aoAbrirTela: (item: ItemMenu) => void;
}) => {
    // Guarda o próprio botão, e não só o retângulo: é dele que sai a âncora a
    // cada render, e é para ele que o foco volta no Escape.
    const [aberto, setAberto] = useState<{
        id: string;
        botao: HTMLButtonElement;
        porTeclado: boolean;
    } | null>(null);
    const timer = useRef<number | null>(null);

    /*
     * Ligada só enquanto o Escape devolve o foco ao ícone. Sem ela, `focus()`
     * dispara o `onFocus` do botão, que reabre o painel que acabamos de fechar —
     * e o Escape só funcionava porque o `setAberto(null)` vinha depois na mesma
     * batch do React. Trocar a ordem daquelas duas linhas quebrava o Escape, e
     * nada no código dizia isso.
     */
    const ignorarProximoFoco = useRef(false);

    /*
     * Roving tabindex no rail, pelo mesmo motivo do modo expandido: o Tab entra
     * uma vez e as setas andam entre os ícones.
     */
    const [focado, setFocado] = useState<string | null>(null);
    const nav = useRef<HTMLElement>(null);

    const ids = arvore.map((no) => String(no.item.ID));
    const focavelId = focado !== null && ids.includes(focado) ? focado : ids[0];

    const focarIndice = (indice: number) => {
        const id = ids[Math.max(0, Math.min(indice, ids.length - 1))];
        if (!id) return;
        setFocado(id);
        nav.current
            ?.querySelector<HTMLButtonElement>(`[data-menu-id="${id}"]`)
            ?.focus();
    };

    const cancelarFechamento = () => {
        if (timer.current !== null) {
            window.clearTimeout(timer.current);
            timer.current = null;
        }
    };

    /*
     * O fechamento é adiado porque entre o ícone e o painel existem 4px de
     * folga: fechar no mouseleave puro faria o submenu sumir no meio do
     * caminho. Entrar no painel — com mouse ou com foco — cancela o
     * agendamento (RN04).
     */
    const agendarFechamento = () => {
        cancelarFechamento();
        timer.current = window.setTimeout(
            () => setAberto(null),
            MS_PARA_FECHAR,
        );
    };

    const fechar = (devolverFoco: boolean) => {
        cancelarFechamento();
        setAberto(null);

        if (!devolverFoco || !aberto) return;

        /*
         * A flag sobe e desce em volta da chamada porque `focus()` despacha o
         * evento de forma síncrona: quando ela volta a false, o `onFocus` já
         * rodou. Assim ela nunca fica presa em true — se um dia o React passar a
         * despachar isso de forma assíncrona, o comportamento degrada para o
         * anterior, que ao menos funcionava, e não para um foco engolido.
         */
        ignorarProximoFoco.current = true;
        aberto.botao.focus();
        ignorarProximoFoco.current = false;
    };

    useEffect(() => cancelarFechamento, []);

    return (
        <nav className="menu-ds-nav menu-ds-nav-colapsado" ref={nav}>
            {arvore.map((no) => {
                const grupo = ehGrupo(no);
                const id = String(no.item.ID);
                const estaAberto = aberto?.id === id;
                const destacado = contemClasseAtiva(no, classeAtiva);
                const Icone = iconeDoMenu(no.item);

                return (
                    <div key={no.item.ID} className="menu-ds-rail-bloco">
                        <button
                            type="button"
                            className={`menu-ds-rail${destacado || estaAberto ? " is-ativo" : ""}`}
                            title={no.item.text}
                            aria-haspopup={grupo ? "menu" : undefined}
                            aria-expanded={grupo ? estaAberto : undefined}
                            data-menu-id={id}
                            tabIndex={focavelId === id ? 0 : -1}
                            onMouseEnter={(e) => {
                                cancelarFechamento();
                                setAberto(
                                    grupo
                                        ? {
                                              id,
                                              botao: e.currentTarget,
                                              porTeclado: false,
                                          }
                                        : null,
                                );
                            }}
                            onMouseLeave={agendarFechamento}
                            // Abrir no foco é o que faz o Tab revelar o submenu.
                            onFocus={(e) => {
                                cancelarFechamento();
                                setFocado(id);
                                // Foco devolvido pelo Escape, não o usuário
                                // chegando de Tab: nada a reabrir.
                                if (ignorarProximoFoco.current) return;
                                if (!grupo) return;
                                setAberto({
                                    id,
                                    botao: e.currentTarget,
                                    porTeclado: false,
                                });
                            }}
                            onBlur={agendarFechamento}
                            onKeyDown={(e) => {
                                const indice = ids.indexOf(id);

                                // Andar entre os ícones vem antes de abrir: vale
                                // para grupo e para raiz que é tela.
                                switch (e.key) {
                                    case "ArrowDown":
                                        e.preventDefault();
                                        return focarIndice(indice + 1);
                                    case "ArrowUp":
                                        e.preventDefault();
                                        return focarIndice(indice - 1);
                                    case "Home":
                                        e.preventDefault();
                                        return focarIndice(0);
                                    case "End":
                                        e.preventDefault();
                                        return focarIndice(ids.length - 1);
                                    case "Escape":
                                        return fechar(false);
                                }

                                if (!grupo) return;
                                if (
                                    e.key !== "Enter" &&
                                    e.key !== " " &&
                                    e.key !== "ArrowRight"
                                )
                                    return;
                                // preventDefault para o Enter não disparar
                                // também o onClick do botão.
                                e.preventDefault();
                                cancelarFechamento();
                                setAberto({
                                    id,
                                    botao: e.currentTarget,
                                    porTeclado: true,
                                });
                            }}
                            onClick={() => {
                                if (no.item.classInvoke) aoAbrirTela(no.item);
                            }}
                        >
                            <Icone size={TAMANHO_ICONE_GRUPO} />
                        </button>

                        {grupo && estaAberto && (
                            <FlyoutSubmenu
                                no={no}
                                ancora={aberto.botao.getBoundingClientRect()}
                                classeAtiva={classeAtiva}
                                aoAbrirTela={aoAbrirTela}
                                aoEntrar={cancelarFechamento}
                                aoSair={agendarFechamento}
                                aoFechar={() => fechar(true)}
                                focarPrimeiroItem={aberto.porTeclado}
                            />
                        )}
                    </div>
                );
            })}
        </nav>
    );
};

export const MenuLateral = ({
    itens,
    classeAtiva,
    colapsado,
    aoAbrirTela,
}: EstadoMenuLateral) => {
    const [busca, setBusca] = useState("");
    const [abertos, setAbertos] = useState<Set<string>>(new Set());
    const [focado, setFocado] = useState<string | null>(null);
    const nav = useRef<HTMLElement>(null);
    const campoBusca = useRef<HTMLInputElement>(null);

    // A busca não existe no modo colapsado; deixar o termo valendo esconderia
    // metade do menu no rail sem nada na tela explicando o porquê.
    const buscaAtiva = colapsado ? "" : busca;

    const filtrados = useMemo(
        () => filterMenuItems(itens, buscaAtiva),
        [itens, buscaAtiva],
    );
    const arvore = useMemo(() => montarArvore(filtrados), [filtrados]);

    const definirAberto = (id: string, aberto: boolean) =>
        setAbertos((atuais) => {
            const proximos = new Set(atuais);
            if (aberto) proximos.add(id);
            else proximos.delete(id);
            return proximos;
        });

    const alternar = (id: string) =>
        setAbertos((atuais) => {
            const proximos = new Set(atuais);
            if (proximos.has(id)) proximos.delete(id);
            else proximos.add(id);
            return proximos;
        });

    // Durante a busca todo grupo que sobrou fica aberto: `filterMenuItems` já
    // devolve só o que casa, e um resultado dentro de grupo fechado seria um
    // resultado invisível.
    const estaAberto = (no: No) =>
        buscaAtiva ? true : abertos.has(String(no.item.ID));

    /* ---- navegação por teclado da árvore (ver Navegacao) ---- */

    const visiveis = useMemo(
        () => achatarVisiveis(arvore, estaAberto),
        // `estaAberto` deriva destes dois; incluí-lo remontaria a cada render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [arvore, abertos, buscaAtiva],
    );

    const indicePorId = useMemo(() => {
        const mapa = new Map<string, number>();
        visiveis.forEach((visivel, indice) =>
            mapa.set(String(visivel.no.item.ID), indice),
        );
        return mapa;
    }, [visiveis]);

    // Cai no primeiro item quando o foco anterior saiu da lista — a busca pode
    // ter filtrado justamente ele, e sem isso o menu perderia a parada de Tab.
    const focavelId =
        focado !== null && indicePorId.has(focado)
            ? focado
            : visiveis.length
              ? String(visiveis[0].no.item.ID)
              : null;

    const focarIndice = (indice: number) => {
        const alvo = visiveis[Math.max(0, Math.min(indice, visiveis.length - 1))];
        if (!alvo) return;
        const id = String(alvo.no.item.ID);
        setFocado(id);
        nav.current
            ?.querySelector<HTMLButtonElement>(`[data-menu-id="${id}"]`)
            ?.focus();
    };

    const aoTeclar = (
        evento: KeyboardEvent<HTMLButtonElement>,
        id: string,
    ) => {
        const indice = indicePorId.get(id);
        if (indice == null) return;

        const { no } = visiveis[indice];
        const grupo = ehGrupo(no);
        const aberto = grupo && estaAberto(no);

        switch (evento.key) {
            case "ArrowDown":
                focarIndice(indice + 1);
                break;
            case "ArrowUp":
                focarIndice(indice - 1);
                break;
            case "Home":
                focarIndice(0);
                break;
            case "End":
                focarIndice(visiveis.length - 1);
                break;
            case "ArrowRight":
                // Fechado, abre; aberto, entra. Dois toques para descer, que é
                // como o dxTreeView se comportava.
                if (!grupo) return;
                if (aberto) focarIndice(indice + 1);
                else definirAberto(id, true);
                break;
            case "ArrowLeft":
                // Aberto, recolhe; senão sobe para o pai.
                if (aberto) {
                    definirAberto(id, false);
                } else {
                    const pai = indiceDoPai(visiveis, indice);
                    if (pai >= 0) focarIndice(pai);
                }
                break;
            default:
                // Enter e Espaço seguem o <button> nativo, que já chama onClick.
                return;
        }

        evento.preventDefault();
    };

    const navegacao: Navegacao = {
        focavelId,
        aoFocar: setFocado,
        aoTeclar,
    };

    return (
        <div className={`menu-ds${colapsado ? " is-colapsado" : ""}`}>
            {!colapsado && (
                <div className="menu-ds-busca">
                    <Search size={12} className="menu-ds-busca-icone" />
                    <input
                        ref={campoBusca}
                        type="text"
                        placeholder="Pesquisar ..."
                        value={busca}
                        onChange={(e) => setBusca(e.target.value)}
                        // Escape limpa, como fazia o showClearButton do
                        // dxTextBox. stopPropagation para o Escape não vazar
                        // para quem escuta a tecla fora do menu.
                        onKeyDown={(e) => {
                            if (e.key !== "Escape" || !busca) return;
                            e.stopPropagation();
                            setBusca("");
                        }}
                    />

                    {busca && (
                        <button
                            type="button"
                            className="menu-ds-busca-limpar"
                            // O foco volta ao campo: quem limpa quer digitar de
                            // novo, não perder o cursor.
                            onClick={() => {
                                setBusca("");
                                campoBusca.current?.focus();
                            }}
                            title="Limpar pesquisa"
                            aria-label="Limpar pesquisa"
                        >
                            <X size={12} />
                        </button>
                    )}
                </div>
            )}

            {colapsado ? (
                <MenuColapsado
                    arvore={arvore}
                    classeAtiva={classeAtiva}
                    aoAbrirTela={aoAbrirTela}
                />
            ) : (
                <nav
                    className="menu-ds-nav"
                    role="tree"
                    aria-label="Menu de navegação"
                    ref={nav}
                >
                    {arvore.map((no) => (
                        <NoMenu
                            key={no.item.ID}
                            no={no}
                            estaAberto={estaAberto}
                            alternar={alternar}
                            classeAtiva={classeAtiva}
                            aoAbrirTela={aoAbrirTela}
                            nav={navegacao}
                        />
                    ))}
                </nav>
            )}
        </div>
    );
};

/**
 * Monta o menu num elemento do DOM e devolve um controle imperativo, porque
 * quem hospeda é MenuNavegacao, que é uma classe jQuery/DevExtreme e não um
 * componente React.
 */
export const criarMenuLateral = (elemento: HTMLElement) => {
    const root: Root = createRoot(elemento);

    let estado: EstadoMenuLateral = {
        itens: [],
        classeAtiva: null,
        colapsado: false,
        aoAbrirTela: () => undefined,
    };

    const desenhar = () => {
        // A classe marca o estado também no elemento hospedeiro, para CSS e
        // depuração. A largura dele NÃO sai daqui: ela acompanha o painel do
        // dxDrawer (`.menu-ds-raiz` em css/menu-ds.css). Enquanto o rail tinha
        // largura própria, ele terminava a transição antes do painel e sobrava
        // uma faixa de fundo escuro ao lado dele (JOR4-7568).
        elemento.classList.toggle("is-colapsado", estado.colapsado);
        root.render(<MenuLateral {...estado} />);
    };

    desenhar();

    return {
        atualizar(parcial: Partial<EstadoMenuLateral>) {
            estado = { ...estado, ...parcial };
            desenhar();
        },
        destruir() {
            root.unmount();
        },
    };
};
